import type { TextPickBatches } from '../shared/bulkEditAiTextPicks';
import type { CountryGenResult } from '../shared/multiCountryBulkEdit.types';
import { batchNeedsEnglishTranslation, countryGenResultHasTexts } from '../shared/translateBulkEditCountryTexts';

import type { AiTextPickInteraction } from './result/aiTextPickInteraction.types';

type PicksArgs = {
  active: string;
  activeResult: CountryGenResult;
  enableTextPicks?: boolean;
  textPicks?: Record<string, TextPickBatches>;
  toggleAiTextPick?: (countryKey: string, batchIdx: number, lineIdx: number) => void;
  setAllAiTextPicksForBatch?: (countryKey: string, batchIdx: number, value: boolean) => void;
  regenerateUnselectedInBatch?: (countryKey: string, batchIdx: number) => void | Promise<void>;
  regenTarget?: { ck: string; batchIdx: number } | null;
  isRegeneratingTexts?: boolean;
  disableTextPickInteractions?: boolean;
  onTranslateCountry?: (countryKey: string) => void | Promise<void>;
  translateMissingInBatch?: (countryKey: string, batchIdx: number) => void | Promise<void>;
  translatingCountryKey?: string | null;
  translatingBatch?: { ck: string; batchIdx: number } | null;
};

export function buildCountryResultsTextPickInteraction(p: PicksArgs): AiTextPickInteraction | undefined {
  const {
    active,
    activeResult,
    enableTextPicks,
    textPicks,
    toggleAiTextPick,
    setAllAiTextPicksForBatch,
    regenerateUnselectedInBatch,
    regenTarget,
    isRegeneratingTexts,
    disableTextPickInteractions,
    onTranslateCountry,
    translateMissingInBatch,
    translatingCountryKey,
    translatingBatch,
  } = p;
  if (
    !enableTextPicks ||
    !toggleAiTextPick ||
    !setAllAiTextPicksForBatch ||
    !regenerateUnselectedInBatch ||
    !countryGenResultHasTexts(activeResult)
  ) {
    return undefined;
  }
  return {
    picks: textPicks?.[active],
    toggle: (b, l) => toggleAiTextPick(active, b, l),
    setAllInBatch: (b, v) => setAllAiTextPicksForBatch(active, b, v),
    regenUnselected: (b) => regenerateUnselectedInBatch(active, b),
    isBatchRegenerating: (b) =>
      Boolean(isRegeneratingTexts && regenTarget?.ck === active && regenTarget.batchIdx === b),
    disableInteractions:
      Boolean(disableTextPickInteractions) ||
      translatingCountryKey === active ||
      translatingBatch?.ck === active,
    enableSectionTranslate: Boolean(onTranslateCountry && translateMissingInBatch),
    translateMissingInBatch: (b) => void translateMissingInBatch?.(active, b),
    isBatchTranslating: (b) => Boolean(translatingBatch?.ck === active && translatingBatch.batchIdx === b),
    batchNeedsEnglishTranslation: (b) => batchNeedsEnglishTranslation(activeResult, b),
  };
}
